/**
 * El lapso de las ventas de un petshop, como en la app: los botones rápidos
 * juntos en una barra y, al lado, el calendario. Lo usan «Ventas y
 * rendiciones» (que además tiene «Sin rendir», lo que se rinde) y «Evaluar
 * ventas». Las fechas se eligen tocando el calendario (no se escriben) y la
 * hora se escribe: la chica del petshop a veces viene a la mañana y otras a
 * la tarde, y las ventas de ese día antes de su visita ya se pagaron.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import { CalendarDays, Check, ChevronLeft, ChevronRight } from 'lucide-react-native';

import { Button, Sheet } from '@/components/ui';
import { colors, typography } from '@/theme/tokens';
import { formatDate } from '@/utils/format';
import type { PetshopVenta } from '@/types/domain';

export { METODO_LABEL, nombreProducto, totalesDe, type Totales } from '@/utils/petshopTotales';

export type Preset = 'pendientes' | 'hoy' | 'ayer' | 'semana' | 'mes' | 'año' | 'todo' | 'custom';

const PRESETS: { id: Preset; label: string }[] = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'ayer', label: 'Ayer' },
  { id: 'semana', label: 'Semana' },
  { id: 'mes', label: 'Mes' },
  { id: 'año', label: 'Año' },
  { id: 'todo', label: 'Todo' },
];

const TINTA2 = '#5A4250';

const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/** «12/10/2026», o «12/10/2026 15:30» si la hora no es la de siempre. */
const conHora = (d: Date, deSiempre: string) => (hhmm(d) === deSiempre ? formatDate(d) : `${formatDate(d)} ${hhmm(d)}`);

const conHoraElegida = (dia: Date, hora: string, segundos: number, ms: number) => {
  const [h = 0, m = 0] = hora.split(':').map(Number);
  return new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), h, m, segundos, ms);
};

const inicioDelDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const finDelDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

function rangoDePreset(preset: Preset): { desde: Date | null; hasta: Date | null } {
  const hoy = new Date();
  const inicioHoy = inicioDelDia(hoy);
  const finHoy = finDelDia(hoy);
  switch (preset) {
    case 'hoy':
      return { desde: inicioHoy, hasta: finHoy };
    case 'ayer': {
      const d = new Date(inicioHoy);
      d.setDate(d.getDate() - 1);
      return { desde: d, hasta: finDelDia(d) };
    }
    case 'semana': {
      const d = new Date(inicioHoy);
      d.setDate(d.getDate() - 6); // últimos 7 días
      return { desde: d, hasta: finHoy };
    }
    case 'mes':
      return { desde: new Date(hoy.getFullYear(), hoy.getMonth(), 1), hasta: finHoy };
    case 'año':
      return { desde: new Date(hoy.getFullYear(), 0, 1), hasta: finHoy };
    default:
      return { desde: null, hasta: null };
  }
}

export interface Periodo {
  preset: Preset;
  desde: Date | null;
  hasta: Date | null;
  elegir: (p: Preset) => void;
  /** Las fechas del calendario, con su hora (las dos incluidas). */
  elegirFechas: (desde: Date, hasta: Date) => void;
  /** Para mostrar y para los archivos: «Sin rendir», «01/10/2026 → 14/10/2026»… */
  texto: string;
}

export function usePeriodo(inicial: Preset): Periodo {
  const [preset, setPreset] = useState<Preset>(inicial);
  const [fechas, setFechas] = useState<{ desde: Date; hasta: Date } | null>(null);

  const { desde, hasta } = useMemo(() => {
    if (preset === 'custom' && fechas) return fechas;
    return rangoDePreset(preset);
  }, [preset, fechas]);

  const texto =
    preset === 'pendientes'
      ? 'Sin rendir'
      : !desde && !hasta
        ? 'Todas las ventas'
        : `${desde ? conHora(desde, '00:00') : '—'} → ${hasta ? conHora(hasta, '23:59') : 'hoy'}`;

  return {
    preset,
    desde,
    hasta,
    elegir: setPreset,
    elegirFechas: (d, h) => {
      setFechas({ desde: d, hasta: h });
      setPreset('custom');
    },
    texto,
  };
}

/** Las ventas del lapso, de la más nueva a la más vieja. */
export function enPeriodo(ventas: PetshopVenta[], desde: Date | null, hasta: Date | null): PetshopVenta[] {
  const arr =
    !desde && !hasta
      ? ventas
      : ventas.filter((v) => {
          const f = v.fecha?.toDate?.();
          if (!f) return true;
          if (desde && f < desde) return false;
          if (hasta && f > hasta) return false;
          return true;
        });
  return [...arr].sort((a, b) => (b.fecha?.toMillis?.() ?? 0) - (a.fecha?.toMillis?.() ?? 0));
}

// ---------------------------------------------------------------------------

export type Vista = 'ventas' | 'productos';

/**
 * La barra de botones rápidos y el calendario. Con fechas elegidas, el
 * calendario queda marcado y al lado se ve el lapso. Con `vista`, también se
 * elige ver cada venta o lo vendido por producto. `acciones` va a la derecha
 * (Excel y PDF).
 */
export function FiltroPeriodo({
  periodo,
  conPendientes = false,
  vista,
  acciones,
}: {
  periodo: Periodo;
  conPendientes?: boolean;
  vista?: { valor: Vista; cambiar: (v: Vista) => void };
  acciones?: ReactNode;
}) {
  const [hoja, setHoja] = useState(false);
  const opciones = conPendientes ? [{ id: 'pendientes' as Preset, label: 'Sin rendir' }, ...PRESETS] : PRESETS;
  const conFechas = periodo.preset === 'custom';
  return (
    <>
    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, paddingHorizontal: 14, paddingVertical: 12 }}>
      <View style={{ flexDirection: 'row', padding: 4, borderRadius: 16, backgroundColor: colors.crema[100] }}>
        {opciones.map((o) => {
          const activo = periodo.preset === o.id;
          return (
            <Pressable
              key={o.id}
              onPress={() => periodo.elegir(o.id)}
              style={({ hovered }: any) => ({
                height: 34,
                paddingHorizontal: 14,
                borderRadius: 12,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: activo ? colors.white : hovered ? 'rgba(255,255,255,0.55)' : 'transparent',
                shadowColor: colors.crema[300],
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: activo ? 1 : 0,
                shadowRadius: 0,
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Text style={{ fontSize: 13, fontWeight: typography.weights.extrabold, color: activo ? colors.magenta[500] : TINTA2 }}>
                {o.label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <Pressable
        onPress={() => setHoja(true)}
        accessibilityLabel="Elegir fechas"
        style={({ hovered }: any) => ({
          height: 42,
          minWidth: 42,
          paddingHorizontal: conFechas ? 12 : 0,
          borderRadius: 21,
          flexDirection: 'row',
          gap: 7,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: conFechas ? colors.magenta[50] : hovered ? colors.crema[50] : colors.white,
          borderWidth: 1,
          borderColor: conFechas ? colors.magenta[200] : colors.crema[300],
          // @ts-ignore web only
          cursor: 'pointer',
        })}
      >
        <CalendarDays size={18} color={conFechas ? colors.magenta[500] : colors.magenta[900]} />
        {conFechas ? (
          <Text style={{ fontSize: 13, fontWeight: typography.weights.extrabold, color: colors.magenta[500] }}>{periodo.texto}</Text>
        ) : null}
      </Pressable>

      {vista ? (
        <View style={{ flexDirection: 'row', padding: 4, borderRadius: 16, backgroundColor: colors.crema[100] }}>
          {([
            ['ventas', 'Ventas'],
            ['productos', 'Por producto'],
          ] as [Vista, string][]).map(([id, texto]) => {
            const activo = vista.valor === id;
            return (
              <Pressable
                key={id}
                onPress={() => vista.cambiar(id)}
                style={({ hovered }: any) => ({
                  height: 34,
                  paddingHorizontal: 14,
                  borderRadius: 12,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: activo ? colors.white : hovered ? 'rgba(255,255,255,0.55)' : 'transparent',
                  shadowColor: colors.crema[300],
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: activo ? 1 : 0,
                  shadowRadius: 0,
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text style={{ fontSize: 13, fontWeight: typography.weights.extrabold, color: activo ? colors.magenta[500] : TINTA2 }}>{texto}</Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}

      {acciones ? <View style={{ flexDirection: 'row', gap: 8, marginLeft: 'auto' }}>{acciones}</View> : null}
    </View>

      <HojaFechas
        visible={hoja}
        desde={conFechas ? periodo.desde : null}
        hasta={conFechas ? periodo.hasta : null}
        onCerrar={() => setHoja(false)}
        onElegir={(d, h) => {
          setHoja(false);
          // La hoja se va con una animación de 180 ms: si la tabla de atrás
          // cambia antes, la animación de la web a veces tira un error.
          setTimeout(() => periodo.elegirFechas(d, h), 220);
        }}
      />
    </>
  );
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const mismoDia = (a: Date | null, b: Date | null) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * «Elegir fechas»: se toca el primer día y después el último (si el segundo
 * queda antes, se dan vuelta). Con un solo día tocado, es ese día.
 */
function HojaFechas({
  visible,
  desde,
  hasta,
  onCerrar,
  onElegir,
}: {
  visible: boolean;
  desde: Date | null;
  hasta: Date | null;
  onCerrar: () => void;
  onElegir: (desde: Date, hasta: Date) => void;
}) {
  const [d, setD] = useState<Date | null>(null);
  const [h, setH] = useState<Date | null>(null);
  const [horaDesde, setHoraDesde] = useState('00:00');
  const [horaHasta, setHoraHasta] = useState('23:59');
  const [mes, setMes] = useState(() => new Date());

  // Al abrir, arranca con lo que estaba elegido (o en el mes de hoy).
  useEffect(() => {
    if (!visible) return;
    setD(desde ? inicioDelDia(desde) : null);
    setH(hasta ? inicioDelDia(hasta) : null);
    setHoraDesde(desde ? hhmm(desde) : '00:00');
    setHoraHasta(hasta ? hhmm(hasta) : '23:59');
    const base = hasta ?? desde ?? new Date();
    setMes(new Date(base.getFullYear(), base.getMonth(), 1));
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const tocar = (dia: Date) => {
    if (!d || h) {
      setD(dia);
      setH(null);
    } else if (dia < d) {
      setH(d);
      setD(dia);
    } else {
      setH(dia);
    }
  };

  const hoy = inicioDelDia(new Date());
  const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
  const vacios = (primero.getDay() + 6) % 7; // la semana empieza el lunes
  const diasDelMes = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
  const celdas: (Date | null)[] = [
    ...Array.from({ length: vacios }, () => null),
    ...Array.from({ length: diasDelMes }, (_, i) => new Date(mes.getFullYear(), mes.getMonth(), i + 1)),
  ];
  while (celdas.length % 7) celdas.push(null);
  const fin = h ?? d;

  return (
    <Sheet
      visible={visible}
      onClose={onCerrar}
      size="sm"
      title="Elegir fechas"
      subtitle="Tocá el primer día y después el último. Si hace falta, escribí la hora."
      footer={
        <Button
          label="Ver ventas"
          iconLeft={<Check size={16} color={colors.white} />}
          fullWidth
          disabled={!d}
          onPress={() => {
            if (!d) return;
            const a = conHoraElegida(d, horaDesde, 0, 0);
            const b = conHoraElegida(fin ?? d, horaHasta, 59, 999);
            // Si en un mismo día quedaron al revés, se dan vuelta.
            if (a > b) onElegir(conHoraElegida(d, horaHasta, 0, 0), conHoraElegida(fin ?? d, horaDesde, 59, 999));
            else onElegir(a, b);
          }}
        />
      }
    >
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <CajaFecha etiqueta="Desde" valor={d} hora={horaDesde} alCambiarHora={setHoraDesde} />
        <CajaFecha etiqueta="Hasta" valor={fin} hora={horaHasta} alCambiarHora={setHoraHasta} />
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 8 }}>
        <BotonMes alTocar={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))} atras />
        <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 17, fontWeight: typography.weights.bold, color: colors.magenta[900] }}>
          {MESES[mes.getMonth()]} {mes.getFullYear()}
        </Text>
        <BotonMes alTocar={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))} />
      </View>

      <View style={{ flexDirection: 'row' }}>
        {DIAS.map((x, i) => (
          <Text key={i} style={{ flex: 1, textAlign: 'center', fontSize: 11.5, fontWeight: typography.weights.extrabold, color: colors.ink[400], paddingVertical: 6 }}>
            {x}
          </Text>
        ))}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {celdas.map((dia, i) => {
          if (!dia) return <View key={i} style={{ width: `${100 / 7}%`, height: 42 }} />;
          const esInicio = mismoDia(dia, d);
          const esFin = mismoDia(dia, fin);
          const extremo = esInicio || esFin;
          const varios = !!d && !!fin && !mismoDia(d, fin);
          const dentro = !!d && !!fin && dia > d && dia < fin;
          return (
            <View
              key={i}
              style={{
                width: `${100 / 7}%`,
                height: 42,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: dentro || (extremo && varios) ? colors.magenta[50] : 'transparent',
                borderTopLeftRadius: esInicio ? 21 : 0,
                borderBottomLeftRadius: esInicio ? 21 : 0,
                borderTopRightRadius: esFin ? 21 : 0,
                borderBottomRightRadius: esFin ? 21 : 0,
              }}
            >
              <Pressable
                onPress={() => tocar(dia)}
                style={({ hovered }: any) => ({
                  width: 38,
                  height: 38,
                  borderRadius: 19,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: extremo ? colors.magenta[500] : hovered ? colors.crema[100] : 'transparent',
                  borderWidth: mismoDia(dia, hoy) && !extremo ? 1.5 : 0,
                  borderColor: colors.magenta[300],
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: extremo ? typography.weights.extrabold : typography.weights.semibold,
                    color: extremo ? colors.white : colors.ink[800],
                  }}
                >
                  {dia.getDate()}
                </Text>
              </Pressable>
            </View>
          );
        })}
      </View>
    </Sheet>
  );
}

function CajaFecha({
  etiqueta,
  valor,
  hora,
  alCambiarHora,
}: {
  etiqueta: string;
  valor: Date | null;
  hora: string;
  alCambiarHora: (h: string) => void;
}) {
  return (
    <View
      style={{
        flex: 1,
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 14,
        backgroundColor: colors.crema[50],
        borderWidth: 1.5,
        borderColor: colors.crema[200],
      }}
    >
      <Text style={{ fontSize: 11, fontWeight: typography.weights.extrabold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {etiqueta}
      </Text>
      <Text style={{ marginTop: 2, fontSize: 15, fontWeight: typography.weights.bold, color: valor ? colors.ink[900] : colors.ink[400] }}>
        {valor ? formatDate(valor) : 'Tocá un día'}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <Text style={{ fontSize: 12.5, fontWeight: typography.weights.semibold, color: colors.ink[500] }}>a las</Text>
        <CampoHora hora={hora} alCambiar={alCambiarHora} />
      </View>
    </View>
  );
}

/** «1830» → «18:30»; «9» → «09:00»; «930» → «09:30». Null si no es una hora. */
function leerHora(texto: string): string | null {
  const n = texto.replace(/\D/g, '');
  if (!n) return null;
  const [h, m] = n.length <= 2 ? [n, '00'] : n.length === 3 ? [n.slice(0, 1), n.slice(1)] : [n.slice(0, 2), n.slice(2, 4)];
  const hh = Number(h);
  const mm = Number(m);
  if (hh > 23 || mm > 59) return null;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/**
 * La hora se escribe. Al tocar el campo queda vacío, con la hora de ahora de
 * guía, y los dos puntos se ponen solos. Con los cuatro números ya vale; al
 * salir, lo que quedó a medias se completa, y si quedó vacío o no es una hora,
 * vuelve la de antes.
 */
function CampoHora({ hora, alCambiar }: { hora: string; alCambiar: (h: string) => void }) {
  const [texto, setTexto] = useState(hora);
  useEffect(() => setTexto(hora), [hora]);
  return (
    <TextInput
      value={texto}
      onChangeText={(v) => {
        const n = v.replace(/\D/g, '').slice(0, 4);
        setTexto(n.length > 2 ? `${n.slice(0, 2)}:${n.slice(2)}` : n);
        if (n.length === 4) {
          const h = leerHora(n);
          if (h) alCambiar(h);
        }
      }}
      onFocus={() => setTexto('')}
      onBlur={() => {
        const h = leerHora(texto);
        if (h) {
          alCambiar(h);
          setTexto(h);
        } else setTexto(hora);
      }}
      inputMode="numeric"
      placeholder={hora}
      placeholderTextColor={colors.ink[400]}
      style={{
        width: 74,
        height: 32,
        paddingHorizontal: 10,
        borderRadius: 10,
        borderWidth: 1.5,
        borderColor: colors.crema[300],
        backgroundColor: colors.white,
        fontSize: 15,
        fontWeight: typography.weights.bold,
        color: colors.ink[900],
        textAlign: 'center',
        // @ts-ignore web only
        outlineStyle: 'none',
      }}
    />
  );
}

function BotonMes({ alTocar, atras = false }: { alTocar: () => void; atras?: boolean }) {
  const Flecha = atras ? ChevronLeft : ChevronRight;
  return (
    <Pressable
      onPress={alTocar}
      accessibilityLabel={atras ? 'Mes anterior' : 'Mes siguiente'}
      style={({ hovered }: any) => ({
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: hovered ? colors.crema[100] : colors.crema[50],
        // @ts-ignore web only
        cursor: 'pointer',
      })}
    >
      <Flecha size={18} color={colors.magenta[900]} />
    </Pressable>
  );
}
